import React from "react";

const PrivacyPolicy = () => {
  return (
    <div className="row">
      <div>
        <div className="card">
          <div className="card-body">
            <h1>Privacy Policy</h1>
            <p>
              <strong>Last updated:</strong> Nov 07, 2025
            </p>

            <p>
              This privacy notice for Delhi Metro Rail Corporation Limited
              ('Company', 'we', 'us', or 'our') describes how and why we might
              collect, store, use, and/or share ('process') your information
              when you use our services ('Services'), such as when you:
            </p>

            <ul>
              <li>
                Download and use our mobile application (DMRC HRMS) or any
                other application of ours that links to this privacy notice
              </li>
              <li>
                Engage with us in other related ways, including any sales,
                marketing, or events
              </li>
            </ul>

            <p>
              <strong>Questions or concerns?</strong> Reading this privacy
              notice will help you understand your privacy rights and choices.
              If you do not agree with our policies and practices, please do not
              use our Services. If you still have any questions or concerns,
              please contact us at{" "}
              <a href="mailto:itappsdmrc@gmail.com">
                itappsdmrc@gmail.com
              </a>
              .
            </p>

            <h2>Summary of Key Points</h2>
            <p>
              This summary provides key points from our privacy notice. You can
              find out more details about any of these topics by following the
              links to each section below:
            </p>

            <ul>
              <li>
                <a href="#what-we-collect">
                  <strong>What personal information do we process?</strong>
                </a>{" "}
                When you visit, use, or navigate our Services, we may process
                personal information depending on how you interact with Delhi
                Metro Rail Corporation Limited and the Services, the choices you
                make, and the products and features you use. Learn more about
                personal information you disclose to us.
              </li>
              <li>
                <a href="#sensitive-info">
                  <strong>Do we process sensitive personal information?</strong>
                </a>{" "}
                We do not process sensitive personal information.
              </li>
              <li>
                <a href="#third-parties">
                  <strong>Do we receive any information from third parties?</strong>
                </a>{" "}
                We do not receive any information from third parties.
              </li>
              <li>
                <a href="#processing-info">
                  <strong>How do we process your information?</strong>
                </a>{" "}
                We process your information to provide, improve, and administer
                our Services, communicate with you, for security and fraud
                prevention, and to comply with law. We may also process your
                information for other purposes with your consent. We process
                your information only when we have a valid legal reason to do
                so.
              </li>
              <li>
                <a href="#sharing-info">
                  <strong>
                    In what situations and with whom do we share personal
                    information?
                  </strong>
                </a>{" "}
                We may share information in specific situations and with specific
                third parties.
              </li>
              <li>
                <a href="#keeping-info-safe">
                  <strong>How do we keep your information safe?</strong>
                </a>{" "}
                We have organisational and technical processes and procedures in
                place to protect your personal information. However, no
                electronic transmission over the internet or information storage
                technology can be guaranteed to be 100% secure.
              </li>
              <li>
                <a href="#your-rights">
                  <strong>What are your rights?</strong>
                </a>{" "}
                Depending on where you are located geographically, the applicable
                privacy law may mean you have certain rights regarding your
                personal information.
              </li>
              <li>
                <a href="#exercise-rights">
                  <strong>How do you exercise your rights?</strong>
                </a>{" "}
                You can contact us, and we will consider and act upon any
                request in accordance with applicable data protection laws.
              </li>
              <li>
                Want to learn more about what Delhi Metro Rail Corporation
                Limited does with any information we collect? Review the privacy
                notice in full.
              </li>
            </ul>

            <h2>Table of Contents</h2>
            <ul>
              <li>
                <a href="#what-we-collect">1. What Information Do We Collect?</a>
              </li>
              <li>
                <a href="#processing-info">
                  2. How Do We Process Your Information?
                </a>
              </li>
              <li>
                <a href="#sharing-info">
                  3. When and With Whom Do We Share Your Personal Information?
                </a>
              </li>
              <li>
                <a href="#data-retention">4. How Long Do We Keep Your Information?</a>
              </li>
              <li>
                <a href="#keeping-info-safe">
                  5. How Do We Keep Your Information Safe?
                </a>
              </li>
              <li>
                <a href="#minors-info">
                  6. Do We Collect Information from Minors?
                </a>
              </li>
              <li>
                <a href="#your-rights">7. What Are Your Privacy Rights?</a>
              </li>
              <li>
                <a href="#dnt-controls">
                  8. Controls for Do-Not-Track Features
                </a>
              </li>
              <li>
                <a href="#california-rights">
                  9. Do California Residents Have Specific Privacy Rights?
                </a>
              </li>
              <li>
                <a href="#updates">10. Do We Make Updates to This Notice?</a>
              </li>
              <li>
                <a href="#contact-us">
                  11. How Can You Contact Us About This Notice?
                </a>
              </li>
              <li>
                <a href="#review-data">
                  12. How Can You Review, Update, or Delete the Data We Collect
                  from You?
                </a>
              </li>
            </ul>

            <h2 id="what-we-collect">1. What Information Do We Collect?</h2>
            <p>
              <strong>Personal information you disclose to us</strong>
            </p>
            <p>
              <strong>In Short: </strong>We collect personal information that you
              voluntarily provide to us when you express an interest in obtaining
              information about us or our products and Services, when you
              participate in activities on the Services, or otherwise when you
              contact us.
            </p>
            <ul>
              <li>no data collected</li>
            </ul>

            <p>
              <strong>Sensitive Information. </strong>We do not process sensitive
              information.
            </p>

            <h2>Application Data</h2>
            <p>
              If you use our application(s), we also may collect the following
              information if you choose to provide us with access or permission:
            </p>

            <ul>
              <li>
                <strong>Mobile Device Data.</strong> We automatically collect
                device information (such as your mobile device ID, model, and
                manufacturer), operating system, version information, and system
                configuration information.
              </li>
              <li>
                If you are using our application(s), we may also collect
                information about your phone network and mobile device
                characteristics.
              </li>
            </ul>

            <p>
              This information is primarily needed to maintain the security and
              operation of our application(s), for troubleshooting, and for
              analytics purposes.
            </p>

            <p>
              All personal information that you provide to us must be true,
              complete, and accurate, and you must notify us of any changes.
            </p>

            <h2 id="processing-info">2. How Do We Process Your Information?</h2>
            <p>
              <strong>In Short:</strong> We process your information to provide,
              improve, and administer our Services, communicate with you, for
              security and fraud prevention, and to comply with the law.
            </p>

            <h2 id="sharing-info">
              3. When and With Whom Do We Share Your Personal Information?
            </h2>
            <p>
              <strong>In Short:</strong> We may share information in specific
              situations described in this section and/or with the following
              third parties.
            </p>
            <ul>
              <li>
                <strong>Business Transfers:</strong> We may share or transfer
                your information in connection with a merger, sale, or
                acquisition.
              </li>
            </ul>

            <h2 id="data-retention">4. How Long Do We Keep Your Information?</h2>
            <p>
              <strong>In Short:</strong> We keep your information for as long as
              necessary to fulfill the purposes outlined in this privacy notice
              unless otherwise required by law.
            </p>

            <h2 id="keeping-info-safe">5. How Do We Keep Your Information Safe?</h2>
            <p>
              <strong>In Short:</strong> We are committed to protecting your
              personal information through organizational and technical security
              measures.
            </p>

            <p>
              However, no electronic transmission or storage technology can be
              guaranteed 100% secure, so we cannot promise that hackers or other
              parties will not gain unauthorized access.
            </p>

            <h2 id="minors-info">6. Do We Collect Information from Minors?</h2>
            <p>
              <strong>In Short:</strong> We do not knowingly collect data from or
              market to children under 18 years of age.
            </p>

            <p>
              If we learn that personal information from users under 18 has been
              collected, we will delete it. If you become aware of such data,
              please contact us at itappsdmrc@gmail.com.
            </p>

            <h2 id="your-rights">7. What Are Your Privacy Rights?</h2>
            <p>
              <strong>In Short:</strong> You may review, change, or terminate your
              account at any time.
            </p>

            <p>
              <strong>Withdrawing your consent:</strong> You can withdraw your
              consent anytime by contacting us.
            </p>

            <p>
              If you have questions or comments about your privacy rights, you
              may email us at{" "}
              <a href="mailto:itappsdmrc@gmail.com">itappsdmrc@gmail.com</a>.
            </p>

            <h2 id="dnt-controls">8. Controls for Do-Not-Track Features</h2>
            <p>
              Most web browsers include a Do-Not-Track ("DNT") feature you can
              activate to signal privacy preferences. Currently, no standard
              exists, so we do not respond to DNT signals.
            </p>

            <h2 id="california-rights">
              9. Do California Residents Have Specific Privacy Rights?
            </h2>
            <p>
              <strong>In Short:</strong> Yes, if you are a resident of California,
              you are granted specific rights regarding access to your personal
              information.
            </p>

            <p>
              California residents can request information about personal data
              disclosed to third parties for marketing purposes.
            </p>

            <h2 id="updates">10. Do We Make Updates to This Notice?</h2>
            <p>
              <strong>In Short:</strong> Yes, we update this notice as necessary
              to stay compliant with relevant laws.
            </p>

            <h2 id="contact-us">11. How Can You Contact Us About This Notice?</h2>
            <p>
              If you have questions or comments about this notice, you may email
              us at{" "}
              <a href="mailto:itappsdmrc@gmail.com">itappsdmrc@gmail.com</a> or
              write to:
            </p>
            <p>
              Delhi Metro Rail Corporation Limited
              <br />
              Fire Brigade Lane, Barakhamba Road
              <br />
              New Delhi - 110001, India
            </p>

            <h2 id="review-data">
              12. How Can You Review, Update, or Delete the Data We Collect from
              You?
            </h2>
            <p>
              Based on your country’s laws, you may have the right to request
              access to, modify, or delete your personal information.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
